import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import WeChatQRDialog from '../common/WeChatQRDialog';
import useWeChatQR from '../../hooks/useWeChatQR';

const ContactCTA = () => {
  const theme = useTheme();
  const { openQR, openWeChatQR, closeWeChatQR } = useWeChatQR();
  const [dialogTitle, setDialogTitle] = useState('免费咨询');

  const contactMethods = [
    {
      icon: <PhoneIcon sx={{ fontSize: 32 }} />,
      title: '电话咨询',
      description: '专业顾问为您答疑解惑',
      action: '立即拨打',
      contact: '0551-62888215 / +86 19956586698',
      color: theme.palette.success.main,
    },
    {
      icon: <EmailIcon sx={{ fontSize: 32 }} />,
      title: '邮件咨询',
      description: '详细需求一对一回复',
      action: '发送邮件',
      contact: 'xihangedu@qq.com',
      color: theme.palette.primary.main,
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 32 }} />,
      title: '预约面谈',
      description: '面对面深度咨询',
      action: '预约时间',
      contact: '免费咨询评估',
      color: theme.palette.secondary.main,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box
      sx={{
        py: 8,
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.1)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-100px',
          left: '-100px',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)}, transparent)`,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Main CTA Section */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant="h2"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                准备开始您的留学之旅？
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  color: theme.palette.text.secondary,
                  maxWidth: 800,
                  mx: 'auto',
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  lineHeight: 1.6,
                }}
              >
                联系我们的专业顾问团队，获取个性化的留学规划和申请指导。
                我们将根据您的背景和目标，为您量身定制最适合的留学方案。
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setDialogTitle('免费评估咨询');
                    openWeChatQR();
                  }}
                  sx={{
                    px: 4,
                    py: 2,
                    borderRadius: 30,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    minWidth: 200,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  免费评估咨询
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setDialogTitle('下载申请指南');
                    openWeChatQR();
                  }}
                  sx={{
                    px: 4,
                    py: 2,
                    borderRadius: 30,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    minWidth: 200,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  下载申请指南
                </Button>
              </Box>
            </motion.div>
          </Box>

          {/* Contact Methods */}
          <Grid container spacing={4}>
            {contactMethods.map((method, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div variants={itemVariants}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 4,
                      borderRadius: 3,
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${alpha(method.color, 0.2)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 16px 40px ${alpha(method.color, 0.15)}`,
                        borderColor: alpha(method.color, 0.4),
                      },
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: alpha(method.color, 0.1),
                        color: method.color,
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      {method.icon}
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {method.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 2,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {method.description}
                    </Typography>

                    {/* Contact Info */}
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 3,
                        color: method.color,
                        fontWeight: 500,
                      }}
                    >
                      {method.contact}
                    </Typography>

                    {/* Action Button */}
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (method.title === '预约面谈') {
                          setDialogTitle('预约面谈咨询');
                          openWeChatQR();
                        } else if (method.title === '电话咨询') {
                          // 优先拨打手机号，因为更常用
                          window.open('tel:+8619956586698');
                        } else if (method.title === '邮件咨询') {
                          window.open(`mailto:${method.contact}`);
                        }
                      }}
                      sx={{
                        backgroundColor: method.color,
                        borderRadius: 20,
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: method.color,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {method.action}
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Additional Info */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                textAlign: 'center',
                mt: 6,
                p: 4,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                💡 专业提醒
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  maxWidth: 800,
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                建议您尽早开始留学规划，通常申请流程需要3-6个月时间。
                我们的顾问将为您制定详细的时间表，确保不错过任何重要截止日期。
              </Typography>
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      <WeChatQRDialog 
        open={openQR} 
        onClose={closeWeChatQR}
        title={dialogTitle}
      />
    </Box>
  );
};

export default ContactCTA;
